import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pentestApi, clientsApi } from '../services/api';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';

const PenetrationTests = () => {
  const navigate = useNavigate();
  const [pentests, setPentests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    client_id: '',
    test_name: '',
    test_type: 'External Network Security Assessment',
    test_date: new Date().toISOString().split('T')[0],
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filterClient, setFilterClient] = useState('');

  const testTypes = [
    'External Network Security Assessment',
    'Internal Network Pentest',
    'Web Application Test',
    'Social Engineering Test',
    'Wireless Security Test'
  ];

  useEffect(() => {
    fetchData();
  }, [filterClient]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = filterClient ? { client_id: filterClient } : {};
      const [pentestsData, clientsData] = await Promise.all([
        pentestApi.list(params).then(res => res.data),
        clientsApi.getAll().then(res => res.data)
      ]);

      setPentests(Array.isArray(pentestsData) ? pentestsData : []);
      setClients(Array.isArray(clientsData) ? clientsData : (clientsData.clients || []));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load penetration tests');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.client_id || !uploadData.test_name || !uploadData.file) {
      setError('Please fill in all required fields and select a file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('client_id', uploadData.client_id);
      formData.append('test_name', uploadData.test_name);
      formData.append('test_type', uploadData.test_type);
      formData.append('test_date', uploadData.test_date);

      const result = await pentestApi.uploadReport(formData);
      
      setSuccess(`Penetration test uploaded successfully! ${result.findings_count} findings extracted.`);
      setUploadDialogOpen(false);
      setUploadData({
        client_id: '',
        test_name: '',
        test_type: 'External Network Security Assessment',
        test_date: new Date().toISOString().split('T')[0],
        file: null
      });
      
      fetchData();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload penetration test');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (pentestId, testName) => {
    if (!window.confirm(`Are you sure you want to delete "${testName}"?`)) {
      return;
    }

    try {
      await pentestApi.delete(pentestId);
      setSuccess('Penetration test deleted successfully');
      fetchData();
    } catch (err) {
      setError('Failed to delete penetration test');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'uploaded': 'default',
      'parsing_failed': 'error',
      'parsed': 'info',
      'analyzing': 'warning',
      'analyzed': 'success',
      'report_generated': 'success'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Penetration Tests
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload New Test
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Filter by Client */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Client</InputLabel>
            <Select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              label="Filter by Client"
            >
              <MenuItem value="">All Clients</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Test Name</strong></TableCell>
                  <TableCell><strong>Client</strong></TableCell>
                  <TableCell><strong>Test Type</strong></TableCell>
                  <TableCell><strong>Test Date</strong></TableCell>
                  <TableCell align="center"><strong>Critical</strong></TableCell>
                  <TableCell align="center"><strong>High</strong></TableCell>
                  <TableCell align="center"><strong>Medium</strong></TableCell>
                  <TableCell align="center"><strong>Total</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pentests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No penetration tests found. Upload your first test to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pentests.map((pentest) => (
                    <TableRow key={pentest.id} hover>
                      <TableCell>{pentest.test_name}</TableCell>
                      <TableCell>{pentest.client_name}</TableCell>
                      <TableCell>{pentest.test_type}</TableCell>
                      <TableCell>{new Date(pentest.test_date).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        {pentest.critical_count > 0 && (
                          <Chip label={pentest.critical_count} color="error" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {pentest.high_count > 0 && (
                          <Chip label={pentest.high_count} color="warning" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {pentest.medium_count > 0 && (
                          <Chip label={pentest.medium_count} color="info" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <strong>{pentest.total_findings}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip label={pentest.status} color={getStatusColor(pentest.status)} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/penetration-tests/${pentest.id}`)}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={() => navigate(`/penetration-tests/${pentest.id}/compare`)}
                          title="Compare / Trending"
                          disabled={!pentest.has_analysis}
                        >
                          <TrendingIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(pentest.id, pentest.test_name)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Penetration Test Report</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Client</InputLabel>
              <Select
                value={uploadData.client_id}
                label="Client"
                onChange={(e) => setUploadData({ ...uploadData, client_id: e.target.value })}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Test Name"
              fullWidth
              value={uploadData.test_name}
              onChange={(e) => setUploadData({ ...uploadData, test_name: e.target.value })}
              placeholder="e.g., External Network Assessment - Q4 2025"
            />

            <FormControl fullWidth>
              <InputLabel>Test Type</InputLabel>
              <Select
                value={uploadData.test_type}
                label="Test Type"
                onChange={(e) => setUploadData({ ...uploadData, test_type: e.target.value })}
              >
                {testTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Test Date"
              type="date"
              fullWidth
              value={uploadData.test_date}
              onChange={(e) => setUploadData({ ...uploadData, test_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <input
                accept=".docx"
                style={{ display: 'none' }}
                id="pentest-file-upload"
                type="file"
                onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
              />
              <label htmlFor="pentest-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  {uploadData.file ? uploadData.file.name : 'Select Technical Report (.docx)'}
                </Button>
              </label>
            </Box>

            <Alert severity="info">
              The system will automatically extract findings from the uploaded technical report.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="primary"
            disabled={uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload & Parse'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PenetrationTests;
